/* ============================================================
   ToTheLove — UGC / avis video (assets/tothelove-ugc-reviews.js)
   Custom element <ttl-ugc-reviews>.

   DEUX MODES, un seul composant
   - ROTATION (desktop >= 990px, si le reglage eventail est actif) :
     les emplacements sont FIXES (petite / moyenne / GRANDE / moyenne /
     petite, centres sur la page) et ce sont les cartes qui tournent a
     travers eux. La grande carte ne bouge donc jamais du centre, seul
     son contenu change. Rotation circulaire : position relative
     (i - active + n) % n -> data-ttl-pos, le CSS transitionne le reste.
     Aucune mesure de largeur, aucun clone de DOM.
     Meme mecanique que <ttl-content-carousel> (carrousel de cartes
     contenu) : si on converge un jour les deux en un composant unique,
     c'est ce modele-la qu'il faut garder.
   - DEFILEMENT (< 990px, ou reglage eventail decoche) : comportement
     historique inchange — piste scroll-snap, swipe natif en tactile,
     fleches + glisser-deposer souris. Le mobile n'est pas touche.
   Le basculement se fait au matchMedia, dans les deux sens.

   Lecture
   - EN ROTATION : seule la carte CENTRALE lit. Elle demarre SEULE et EN
     MUET quand la section entre dans le champ (IntersectionObserver) et
     a chaque rotation. Les cartes laterales ne reagissent pas au clic
     (et n'affichent aucune commande, cf. CSS).
     Exception au muet : si la personne a demande le son via la pastille,
     on le conserve d'une carte a l'autre — un geste utilisateur a bien eu
     lieu, donc le navigateur l'autorise, et sans ca il faudrait
     reactiver le son a chaque rotation.
   - EN DEFILEMENT (mobile) : comportement historique, clic = play/pause
     sur n'importe quelle carte, aucun demarrage automatique.
   - Clic sur la video (ou le bouton play) -> play/pause inline.
   - Une seule video joue a la fois dans la section. Toute rotation met
     en pause : sinon une bande son continue depuis une carte partie sur
     le cote.
   - prefers-reduced-motion : aucun demarrage automatique (une video qui
     part seule est un mouvement non sollicite).
   - SON (reglage de section) : si l'attribut data-ttl-sound est
     present, le clic est un geste utilisateur -> on de-mute AVANT
     play(). Repli automatique en muet si le navigateur refuse.
     Sans cet attribut : muted force avant chaque play (le rendu de
     l'attribut muted par video_tag n'est pas fiable -> on ne s'y fie
     jamais).
   - Pastille son [data-ttl-ugc-sound] : coupe / reactive le son de la
     carte sans interrompre la lecture.
   - Etat visuel + libelles a11y pilotes par les evenements natifs
     play / pause / ended / volumechange (jamais par le clic).

   Navigation
   - Fleches [data-ttl-ugc-arrow] : en rotation elles font tourner d'un
     cran et ne sont JAMAIS desactivees (la boucle n'a pas d'extremite) ;
     en defilement elles visent la carte suivante / precedente reellement
     presente dans le DOM (les cartes n'ont pas toutes la meme largeur).
   - Glisser-deposer souris : en rotation, un glissement franc fait
     tourner d'un cran ; en defilement, il pilote scrollLeft.
     ATTENTION (bug S11) : on ne capture le pointeur qu'APRES le seuil
     de glissement, sinon le `click` est redispatche sur la piste au
     lieu du bouton play et la lecture ne se declenche jamais.

   Libelles accessibilite fournis par Liquid via data-ttl-label-*
   (aucun texte hardcode ici). Scoping strict [data-ttl-ugc-*].
   ============================================================ */

if (!customElements.get('ttl-ugc-reviews')) {
  const DRAG_THRESHOLD = 8; // px avant de considerer un glissement plutot qu'un clic
  const SWIPE_ROTATE = 40; // px de glissement franc pour faire tourner d'un cran
  const CAROUSEL_MQ = '(min-width: 990px)'; // doit rester aligne sur le CSS
  const SLOTS_VISIBLE = 5; // petite / moyenne / GRANDE / moyenne / petite

  customElements.define(
    'ttl-ugc-reviews',
    class TTLUgcReviews extends HTMLElement {
      connectedCallback() {
        this.labelPlay = this.dataset.ttlLabelPlay || '';
        this.labelPause = this.dataset.ttlLabelPause || '';
        this.labelSoundOn = this.dataset.ttlLabelSoundOn || '';
        this.labelSoundOff = this.dataset.ttlLabelSoundOff || '';

        // Pose par la section quand le reglage "Activer le son" est coche.
        this.soundEnabled = this.hasAttribute('data-ttl-sound');
        // Pose par la section quand le reglage "Cartes en eventail" est coche.
        this.fanEnabled = this.hasAttribute('data-ttl-ugc-fan');

        this.track = this.querySelector('[data-ttl-ugc-track]');
        this.cards = this.track ? Array.from(this.track.children) : [];

        // La carte du MILIEU est celle mise en avant au chargement : l'ordre des
        // blocks dans l'editeur reste lisible pour la personne qui monte la page.
        this.active = Math.max(0, Math.floor((this.cards.length - 1) / 2));

        // Vrai pendant le clic qui suit immediatement un glissement souris :
        // on le "consomme" pour ne pas toggler une video par accident.
        this._suppressClick = false;

        // Pose par l'IntersectionObserver. Tant que la section n'est pas au
        // niveau du lecteur, rien ne demarre.
        this.inView = false;
        // Passe a vrai des que la personne reclame le son via la pastille.
        this.userWantsSound = false;

        this.onClick = (event) => {
          // 1) Fleches. Traitees AVANT le garde-fou de glissement : une
          //    fleche n'est jamais draggable, son clic ne doit pas etre mange.
          const arrow = event.target.closest('[data-ttl-ugc-arrow]');
          if (arrow && this.contains(arrow)) {
            const direction = arrow.dataset.ttlUgcArrow === 'prev' ? -1 : 1;
            if (this.carouselOn) {
              this.rotate(direction);
            } else {
              this.scrollByCard(direction);
            }
            return;
          }

          // 2) Fin de glissement souris -> on ignore ce clic.
          if (this._suppressClick) {
            this._suppressClick = false;
            return;
          }

          // 3) Pastille son : agit sur le volume, sans toggler la lecture.
          const soundToggle = event.target.closest('[data-ttl-ugc-sound]');
          if (soundToggle && this.contains(soundToggle)) {
            const soundMedia = soundToggle.closest('[data-ttl-ugc-media]');
            const soundVideo = soundMedia && soundMedia.querySelector('video');
            if (soundVideo) {
              soundVideo.muted = !soundVideo.muted;
              if (!soundVideo.muted) soundVideo.volume = 1;
              // Report du choix sur les cartes suivantes.
              this.userWantsSound = !soundVideo.muted;
            }
            return;
          }

          // 4) Media : lecture / pause. En rotation, SEULE la carte centrale
          //    est lisible — le clic sur une laterale ne fait rien.
          const media = event.target.closest('[data-ttl-ugc-media]');
          if (!media || !this.contains(media)) return;

          if (this.carouselOn) {
            const card = media.closest('[data-ttl-pos]');
            if (!card || card.dataset.ttlPos !== 'center') return;
          }

          const video = media.querySelector('video');
          if (!video) return;

          if (video.paused) {
            this.playOne(video);
          } else {
            video.pause();
          }
        };

        this.addEventListener('click', this.onClick);

        // Etat visuel + libelles pilotes par les evenements natifs de la video
        // (robuste meme si la lecture est interrompue par le navigateur).
        this.onMediaState = (event) => {
          const video = event.target;
          if (!video || typeof video.closest !== 'function') return;

          const media = video.closest('[data-ttl-ugc-media]');
          if (!media || !this.contains(media)) return;

          const playing = !video.paused && !video.ended;
          media.classList.toggle('ttl-ugc__media--playing', playing);

          const playButton = media.querySelector('[data-ttl-ugc-play]');
          if (playButton) {
            const label = playing ? this.labelPause : this.labelPlay;
            if (label) playButton.setAttribute('aria-label', label);
          }

          const soundToggle = media.querySelector('[data-ttl-ugc-sound]');
          if (soundToggle) {
            const muted = video.muted || video.volume === 0;
            soundToggle.setAttribute('data-ttl-muted', muted ? 'true' : 'false');
            const label = muted ? this.labelSoundOn : this.labelSoundOff;
            if (label) soundToggle.setAttribute('aria-label', label);
          }
        };

        // Les evenements media ne remontent pas -> ecoute en capture.
        this.mediaEvents = ['play', 'pause', 'ended', 'volumechange'];
        this.mediaEvents.forEach((type) => {
          this.addEventListener(type, this.onMediaState, true);
        });

        if (this.track) {
          this.bindDrag();

          this.onScroll = () => {
            if (!this.carouselOn) this.updateArrows();
          };
          this.track.addEventListener('scroll', this.onScroll, { passive: true });

          this.onResize = () => this.applyMode();
          window.addEventListener('resize', this.onResize);

          // Basculement rotation <-> defilement au franchissement du palier.
          if (window.matchMedia) {
            this.mq = window.matchMedia(CAROUSEL_MQ);
            this.onMq = () => this.applyMode();
            if (this.mq.addEventListener) {
              this.mq.addEventListener('change', this.onMq);
            } else if (this.mq.addListener) {
              this.mq.addListener(this.onMq); // Safari < 14
            }
          }

          // Rien ne demarre tant que la section n'est pas au niveau du lecteur.
          // rootMargin negatif plutot qu'un seuil en pourcentage : un seuil de
          // 40% ne se declenche jamais si la section est plus haute que la
          // fenetre. La, il suffit qu'elle croise la bande centrale de l'ecran.
          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  this.inView = entry.isIntersecting;
                  if (entry.isIntersecting) {
                    this.playCenter();
                  } else {
                    this.pauseAll();
                  }
                });
              },
              { threshold: 0, rootMargin: '-15% 0px -15% 0px' }
            );
            this.observer.observe(this);
          } else {
            this.inView = true;
          }

          this.applyMode();
          this.bindAccordionRefresh();
          // Les largeurs / hauteurs ne sont fiables qu'apres un tour de rendu.
          requestAnimationFrame(() => {
            this.applyMode();
            // Les transitions n'entrent en jeu qu'apres le premier placement,
            // sinon les cartes arrivent en glissant depuis le centre.
            this.setAttribute('data-ttl-ugc-ready', '');
          });
          // Jost est charge en display: swap -> les hauteurs bougent apres coup.
          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.setStageHeight());
          }
        }
      }

      bindAccordionRefresh() {
        this.accordionHost = this.closest('.accordion-container');
        if (!this.accordionHost) return;
        this.onAccordionClick = () => {
          window.setTimeout(() => this.applyMode(), 280);
        };
        this.accordionHost.addEventListener('click', this.onAccordionClick);
      }

      /* ---------- Choix du mode ---------- */

      get carouselOn() {
        return this.hasAttribute('data-ttl-ugc-carousel');
      }

      applyMode() {
        if (!this.track) return;

        const was = this.carouselOn;
        const wide = this.mq ? this.mq.matches : true;
        // En dessous de 3 cartes il n'y a aucun emplacement lateral a remplir :
        // la rotation n'aurait rien a faire tourner.
        const on = this.fanEnabled && wide && this.cards.length >= 3;

        if (on) {
          // Hauteur posee AVANT le passage en absolu : sinon la piste s'effondre
          // le temps que setStageHeight tourne (CLS au chargement desktop).
          let preHeight = 0;
          this.cards.forEach((card) => {
            if (card.offsetHeight > preHeight) preHeight = card.offsetHeight;
          });
          if (preHeight) this.track.style.height = preHeight + 'px';

          this.setAttribute('data-ttl-ugc-carousel', '');
          this.layout();
          // Les fleches n'ont d'interet que s'il existe des cartes hors champ.
          this.toggleAttribute(
            'data-ttl-ugc-scrollable',
            this.cards.length > SLOTS_VISIBLE
          );
          // La boucle n'a pas d'extremite : jamais de fleche desactivee.
          this.querySelectorAll('[data-ttl-ugc-arrow]').forEach((button) => {
            button.disabled = false;
          });
          // Uniquement a la BASCULE : applyMode() est rappele a chaque resize,
          // et relancer la lecture a chaque pixel redemarrerait une video que
          // la personne vient de mettre en pause.
          if (!was) this.playCenter();
        } else {
          if (was) this.pauseAll(); // on ne laisse pas jouer hors du mode
          this.removeAttribute('data-ttl-ugc-carousel');
          this.track.style.height = '';
          this.cards.forEach((card) => {
            card.removeAttribute('data-ttl-pos');
            card.removeAttribute('aria-hidden');
            if ('inert' in card) card.inert = false;
          });
          this.updateArrows();
        }
      }

      /* ---------- Mode rotation ---------- */

      // Position relative de chaque carte a l'active, ramenee a un emplacement.
      // Au-dela des emplacements visibles, la carte est garee du cote par lequel
      // elle reviendra : a droite si elle est "devant" dans le cycle, a gauche
      // sinon. C'est ce qui rend la boucle lisible plutot que teleportee.
      slotFor(index) {
        const n = this.cards.length;
        const r = (index - this.active + n) % n;
        if (r === 0) return 'center';

        // Deux emplacements de chaque cote. Une carte peut etre eligible aux
        // deux quand il y a peu de cartes (a 3 cartes, la 3e est aussi bien "a
        // deux crans a droite" qu'"a un cran a gauche") : on prend le cote le
        // plus proche, sinon tout s'entasserait a droite.
        const right = r <= 2 ? r : null;
        const left = n - r <= 2 ? n - r : null;

        if (right !== null && (left === null || right <= left)) return 'right-' + right;
        if (left !== null) return 'left-' + left;

        // Hors champ : garee du cote par lequel elle reviendra.
        return r < n / 2 ? 'park-right' : 'park-left';
      }

      layout() {
        this.cards.forEach((card, index) => {
          const slot = this.slotFor(index);
          card.setAttribute('data-ttl-pos', slot);

          // Une carte garee est hors champ : on la sort du parcours clavier et
          // des lecteurs d'ecran, sinon on tabule dans le vide.
          const parked = slot.indexOf('park') === 0;
          if ('inert' in card) card.inert = parked;
          if (parked) {
            card.setAttribute('aria-hidden', 'true');
          } else {
            card.removeAttribute('aria-hidden');
          }
        });
        this.setStageHeight();
      }

      // Les cartes sont en absolu : elles ne portent plus la hauteur du bloc.
      // On la pose depuis la plus haute (transform n'affecte pas offsetHeight).
      setStageHeight() {
        if (!this.carouselOn || !this.track) return;
        let height = 0;
        this.cards.forEach((card) => {
          if (card.offsetHeight > height) height = card.offsetHeight;
        });
        if (height) this.track.style.height = height + 'px';
      }

      centerVideo() {
        const card = this.cards.find(
          (item) => item.getAttribute('data-ttl-pos') === 'center'
        );
        return card ? card.querySelector('video') : null;
      }

      get reduceMotion() {
        return !!(
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      // Demarrage automatique de la carte centrale. MUET par defaut : aucun
      // navigateur n'autorise un autoplay sonore sans geste utilisateur. Le son
      // n'est conserve que si la personne l'a explicitement reclame via la
      // pastille (retirer `&& this.userWantsSound` pour forcer le muet partout).
      playCenter() {
        if (!this.carouselOn || !this.inView || this.reduceMotion) return;

        const video = this.centerVideo();
        if (!video) return;

        this.pauseAll(video);

        const withSound = this.soundEnabled && this.userWantsSound;
        video.muted = !withSound;
        if (withSound) video.volume = 1;

        const played = video.play();
        if (played && typeof played.catch === 'function') {
          played.catch(() => {
            // Refus du navigateur : on retombe en muet plutot que sur une
            // vignette figee.
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      }

      rotate(direction) {
        const n = this.cards.length;
        if (!n) return;
        // Sans ca, la carte partie sur le cote continue de jouer hors champ.
        this.pauseAll();
        this.active = (this.active + direction + n) % n;
        this.layout();
        // La nouvelle carte centrale prend le relais immediatement.
        this.playCenter();
      }

      /* ---------- Lecture ---------- */

      playOne(video) {
        // Une seule a la fois : sinon deux bandes son se superposent.
        this.pauseAll(video);

        if (!this.soundEnabled) {
          video.muted = true;
          video.play().catch(() => {});
          return;
        }

        // Le play() part d'un clic (geste utilisateur) -> l'audio est autorise.
        video.muted = false;
        video.volume = 1;

        const played = video.play();
        if (played && typeof played.catch === 'function') {
          played.catch(() => {
            // Repli : si le navigateur refuse la lecture avec le son, on
            // rejoue en muet pour au moins afficher l'image.
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      }

      pauseAll(except) {
        this.querySelectorAll('[data-ttl-ugc-media] video').forEach((video) => {
          if (video !== except) video.pause();
        });
      }

      /* ---------- Mode defilement ---------- */

      // Position de defilement qui amene une carte au bord gauche de la piste.
      // offsetLeft est relatif au premier ancetre positionne (l'hote), pas a la
      // piste : on raisonne donc en ECART avec la premiere carte, qui est par
      // definition a scrollLeft = 0. Robuste au centrage de la piste.
      cardTargets() {
        if (!this.track) return [];
        const cards = Array.from(this.track.children);
        if (!cards.length) return [];
        const origin = cards[0].offsetLeft;
        return cards.map((card) => card.offsetLeft - origin);
      }

      scrollByCard(direction) {
        if (!this.track) return;
        const targets = this.cardTargets();
        if (!targets.length) return;

        const pos = this.track.scrollLeft;
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        let target;

        if (direction > 0) {
          // Premiere carte strictement a droite de la position courante.
          target = targets.find((value) => value > pos + 1);
          if (target == null) target = max;
        } else {
          // Derniere carte strictement a gauche.
          const before = targets.filter((value) => value < pos - 1);
          target = before.length ? before[before.length - 1] : 0;
        }

        // La derniere carte ne peut pas etre amenee au bord gauche : on borne,
        // sinon la fleche "suivant" parait morte sur le dernier ecran.
        target = Math.min(Math.max(target, 0), max);

        const reduce =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
      }

      updateArrows() {
        if (!this.track || this.carouselOn) return;

        const max = this.track.scrollWidth - this.track.clientWidth;
        const scrollable = max > 1;

        // Pilote l'affichage des fleches en CSS (rien a masquer en JS).
        this.toggleAttribute('data-ttl-ugc-scrollable', scrollable);

        const pos = this.track.scrollLeft;
        this.querySelectorAll('[data-ttl-ugc-arrow]').forEach((button) => {
          const isPrev = button.dataset.ttlUgcArrow === 'prev';
          button.disabled = !scrollable || (isPrev ? pos <= 1 : pos >= max - 1);
        });
      }

      /* ---------- Glisser-deposer souris (les deux modes) ---------- */

      bindDrag() {
        const track = this.track;
        let down = false;
        let isMouse = false;
        let startX = 0;
        let startScroll = 0;
        let moved = false;
        let dx = 0;

        this.onPointerDown = (event) => {
          this._suppressClick = false; // on repart propre a chaque interaction
          isMouse = event.pointerType === 'mouse';
          if (!isMouse) return; // tactile : on laisse le scroll natif
          down = true;
          moved = false;
          dx = 0;
          startX = event.clientX;
          startScroll = track.scrollLeft;
          // IMPORTANT : pas de setPointerCapture ici (cf. en-tete du fichier).
        };

        this.onPointerMove = (event) => {
          if (!down || !isMouse) return;
          dx = event.clientX - startX;
          if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
            moved = true;
            this._suppressClick = true;
            track.setAttribute('data-ttl-grabbing', ''); // coupe le snap (CSS) + curseur
            try {
              track.setPointerCapture(event.pointerId);
            } catch (error) {
              /* noop */
            }
          }
          if (moved) {
            event.preventDefault(); // evite la selection de texte / le drag d'image
            // En rotation il n'y a rien a faire defiler : le glissement est
            // interprete au relachement, en un cran.
            if (!this.carouselOn) track.scrollLeft = startScroll - dx;
          }
        };

        this.onPointerUp = (event) => {
          if (!down) return;
          down = false;
          track.removeAttribute('data-ttl-grabbing');
          if (event && event.pointerId != null) {
            try {
              track.releasePointerCapture(event.pointerId);
            } catch (error) {
              /* noop */
            }
          }
          if (this.carouselOn && moved && Math.abs(dx) > SWIPE_ROTATE) {
            // Glisser vers la gauche = avancer dans la piste.
            this.rotate(dx < 0 ? 1 : -1);
          }
        };

        track.addEventListener('pointerdown', this.onPointerDown);
        track.addEventListener('pointermove', this.onPointerMove);
        track.addEventListener('pointerup', this.onPointerUp);
        track.addEventListener('pointercancel', this.onPointerUp);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        (this.mediaEvents || []).forEach((type) => {
          this.removeEventListener(type, this.onMediaState, true);
        });
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.observer) this.observer.disconnect();
        if (this.mq && this.onMq) {
          if (this.mq.removeEventListener) {
            this.mq.removeEventListener('change', this.onMq);
          } else if (this.mq.removeListener) {
            this.mq.removeListener(this.onMq);
          }
        }
        if (this.accordionHost && this.onAccordionClick) {
          this.accordionHost.removeEventListener('click', this.onAccordionClick);
        }
        if (this.track) {
          if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
          this.track.removeEventListener('pointerdown', this.onPointerDown);
          this.track.removeEventListener('pointermove', this.onPointerMove);
          this.track.removeEventListener('pointerup', this.onPointerUp);
          this.track.removeEventListener('pointercancel', this.onPointerUp);
        }
      }
    }
  );
}
